import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Lock, Loader2, ArrowRight, Shield, Mail, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import "@/components/FooterHero.css";

const PHOTOS = [
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/ia3f3hx0_DJI_0047.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/j1fgw5f5__MG_4220.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/nzzupzzg__MG_4670.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/8lcqa08z__MG_4532.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/ka7mdxbx_Garden-071.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/ur9t5zbt_R%26C%20Behind%20the%20brand.jpg",
  "https://customer-assets.emergentagent.com/job_project-hub-697/artifacts/u4ui9zrs_CRESSBROOK%20%21.jpg",
];

function shuffle(a) {
  const x = [...a];
  for (let i = x.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [x[i], x[j]] = [x[j], x[i]];
  }
  return x;
}

export function AuthScene({ children }) {
  const slides = useMemo(() => shuffle(PHOTOS), []);
  const SLIDE = 7;
  const total = slides.length * SLIDE;
  return (
    <section className="fh-section" style={{ minHeight: "100vh" }} data-testid="auth-scene">
      <div className="fh-image-wrap">
        {slides.map((url, i) => (
          <div
            key={`${url}-${i}`}
            className="fh-image"
            style={{
              backgroundImage: `url('${url}')`,
              animationName: "fh-slide",
              animationTimingFunction: "ease-in-out",
              animationIterationCount: "infinite",
              animationFillMode: "both",
              animationDuration: `${total}s`,
              animationDelay: `${i * SLIDE}s`,
            }}
          />
        ))}
      </div>
      <div className="fh-overlay" style={{ opacity: 0.78 }} />
      <HexBackdrop />
      <div className="fh-content" style={{ flex: 1, minHeight: "100vh" }}>
        <div className="fh-inner" style={{ maxWidth: 520 }}>
          {children}
        </div>
      </div>
    </section>
  );
}

export function AuthCard({ children }) {
  return (
    <div
      className="rounded-2xl p-5 sm:p-7"
      style={{
        background: "rgba(17, 24, 39, 0.55)",
        border: "1px solid rgba(255,255,255,0.12)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: "0 20px 60px -20px rgba(0,0,0,0.7)",
      }}
    >
      {children}
    </div>
  );
}

export function AuthInput({ label, type = "text", value, onChange, placeholder, icon, testId, autoFocus, autoComplete }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <label className="block mb-4">
      <span className="block text-[11px] uppercase tracking-[0.18em] text-amber-300 font-bold mb-2">
        {label}
      </span>
      <div className="relative">
        {icon ? <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span> : null}
        <input
          type={isPassword && show ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          className={`w-full rounded-lg bg-white/95 text-gray-900 ${icon ? "pl-9" : "px-3"} ${isPassword ? "pr-10" : "pr-3"} py-3 text-base outline-none focus:ring-2 focus:ring-amber-300`}
          placeholder={placeholder}
          data-testid={testId}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            aria-label={show ? "Hide password" : "Show password"}
            data-testid={testId ? `${testId}-toggle` : undefined}
            tabIndex={-1}
          >
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      const to = location.state?.from?.pathname || (user.must_change_password ? "/welcome" : "/");
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      const to = u.must_change_password ? "/welcome" : (location.state?.from?.pathname || "/");
      navigate(to, { replace: true });
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Sign in failed. Check your email and password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScene>
      <div className="fh-eyebrow" style={{ color: "#FFE066" }}>
        R&amp;C Williams · Project Portal
      </div>
      <h1 className="fh-heading" style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}>
        Sign in
      </h1>
      <p className="fh-text" style={{ maxWidth: "44ch" }}>
        Submit projects, manage your case studies and view the team's work.
      </p>
      <AuthCard>
        <form onSubmit={submit}>
          <AuthInput
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="you@randcwilliams.co.uk"
            icon={<Mail className="h-4 w-4" />}
            testId="login-email"
            autoFocus
            autoComplete="email"
          />
          <AuthInput
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            placeholder="••••••••"
            icon={<Lock className="h-4 w-4" />}
            testId="login-password"
            autoComplete="current-password"
          />
          {err ? (
            <div className="text-sm text-red-300 mb-3" data-testid="login-error">{err}</div>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className="fh-btn-primary w-full"
            data-testid="login-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="mt-4 flex items-center justify-between text-xs text-white/70">
            <Link to="/forgot-password" className="hover:text-amber-300 underline-offset-4 hover:underline" data-testid="login-forgot">
              Forgot password?
            </Link>
            <span className="inline-flex items-center gap-2 text-white/55">
              <Shield className="h-3.5 w-3.5" /> R&amp;C staff only
            </span>
          </div>
        </form>
      </AuthCard>
    </AuthScene>
  );
}

function HexBackdrop() {
  return (
    <svg
      className="fh-svg"
      viewBox="0 0 1200 700"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      style={{ opacity: 0.85 }}
    >
      <g className="fh-hex-base">
        {hexCluster().map((p, i) => <path key={i} d={p} />)}
      </g>
      <g className="fh-hex-chase">
        {hexCluster().map((p, i) => {
          const dur = 8 + (i % 6) * 2;
          const delay = (i * 0.4) % 3;
          const dir = i % 3 === 0 ? "reverse" : "normal";
          const accent = i % 5 === 0;
          return (
            <path
              key={`c${i}`}
              d={p}
              pathLength="100"
              className="fh-chase"
              style={{
                "--dur": `${dur}s`,
                "--delay": `${delay}s`,
                animationDirection: dir,
                stroke: accent ? "#FFE066" : "rgba(255,255,255,0.7)",
              }}
            />
          );
        })}
      </g>
    </svg>
  );
}

function hexCluster() {
  const R = 50;
  const SQRT3 = Math.sqrt(3);
  const STEP_X = SQRT3 * R;
  const STEP_Y = 1.5 * R;
  const positions = [];
  for (let row = 0; row < 5; row++) {
    const y = 690 - row * STEP_Y;
    const offset = row % 2 === 0 ? 0 : STEP_X / 2;
    for (let col = 0; col < 5 - Math.floor(row / 2); col++) {
      positions.push([1170 - offset - col * STEP_X, y]);
    }
  }
  for (let row = 0; row < 3; row++) {
    const y = 60 + row * STEP_Y;
    const offset = row % 2 === 0 ? 0 : STEP_X / 2;
    for (let col = 0; col < 3; col++) {
      positions.push([60 + offset + col * STEP_X, y]);
    }
  }
  return positions.map(([cx, cy]) => {
    const pts = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i + Math.PI / 6;
      pts.push([cx + R * Math.cos(a), cy + R * Math.sin(a)]);
    }
    return (
      `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)} ` +
      pts.slice(1).map((p) => `L ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ") +
      " Z"
    );
  });
}
